import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// =============================================================================
//  Types
// =============================================================================

export interface FeatureFlagsResponse {
  flags: Record<string, boolean>;
}

interface FeatureFlagsContextValue {
  flags: Record<string, boolean>;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

// =============================================================================
//  Anonymous bucket ID (D13)
// =============================================================================

const ANON_STORAGE_KEY = 'ff_anon_id';

/**
 * Returns a stable anonymous UUID persisted in localStorage. Generated on the
 * first call; reused thereafter. Returns empty string when storage is
 * unavailable (Safari ITP strict, private mode, SSR) — callers SHOULD omit
 * the header in that case.
 */
export function getOrCreateAnonId(): string {
  try {
    if (typeof localStorage === 'undefined') return '';
    let id = localStorage.getItem(ANON_STORAGE_KEY);
    if (!id) {
      if (typeof crypto?.randomUUID === 'function') {
        id = crypto.randomUUID();
      } else {
        // Fallback for older browsers
        id =
          'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
      }
      localStorage.setItem(ANON_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

// =============================================================================
//  Provider
// =============================================================================

export interface FeatureFlagsProviderProps {
  /** Endpoint URL of the audience-specific feature flags route. */
  endpoint: string;
  /** Polling interval in milliseconds. Default 5 minutes. */
  refreshIntervalMs?: number;
  /**
   * Espera, em milissegundos, antes da primeira nova tentativa depois de uma
   * leitura que falhou. Dobra a cada falha seguida, sem passar de
   * `refreshIntervalMs`. Padrão 2 segundos.
   */
  retryBaseDelayMs?: number;
  /**
   * Optional fetch implementation override (useful for tests or consumers
   * needing to inject auth headers via a wrapped fetch).
   */
  fetcher?: typeof fetch;
  children: ReactNode;
}

const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000;
const DEFAULT_RETRY_BASE_DELAY = 2_000;

/**
 * Espera nominal antes da nova tentativa número `failures` (a partir de 1):
 * `baseMs`, e o dobro a cada falha seguida, até `maxMs`. A espera real sorteia
 * entre metade e o total dela, para que as abas que perderam a API juntas não
 * voltem todas no mesmo instante.
 */
export function retryDelayMs(failures: number, baseMs: number, maxMs: number): number {
  const exponent = Math.min(Math.max(failures - 1, 0), 30);
  return Math.min(baseMs * 2 ** exponent, maxMs);
}

/** Resposta HTTP fora de 2xx, com o status para o aviso no console. */
class FeatureFlagsHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.name = 'FeatureFlagsHttpError';
    this.status = status;
  }
}

/** Resposta 2xx que não traz o mapa `flags`: vale como falha, não como "tudo desligado". */
class FeatureFlagsPayloadError extends Error {
  constructor() {
    super('invalid payload');
    this.name = 'FeatureFlagsPayloadError';
  }
}

function isFlagsMap(value: unknown): value is Record<string, boolean> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * O motivo da falha, para o console: só o status HTTP ou o tipo do erro.
 * A mensagem fica de fora — um `fetcher` do SPA pode lançar erro com qualquer
 * coisa dentro, e o console do navegador do cliente não é lugar de dado.
 */
function describeFailure(err: unknown): string {
  if (err instanceof FeatureFlagsHttpError) return err.message;
  if (err instanceof FeatureFlagsPayloadError) return 'resposta sem o mapa de flags';
  const name = (err as { name?: unknown } | null)?.name;
  return typeof name === 'string' && name ? name : 'erro desconhecido';
}

async function requestFlags(
  endpoint: string,
  fetcher: typeof fetch | undefined,
  signal: AbortSignal,
): Promise<Record<string, boolean>> {
  const anonId = getOrCreateAnonId();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (anonId) headers['X-Anonymous-Id'] = anonId;
  const fetchImpl = fetcher ?? fetch;
  const response = await fetchImpl(endpoint, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new FeatureFlagsHttpError(response.status);
  }
  const data = (await response.json()) as Partial<FeatureFlagsResponse> | null;
  const flags = data?.flags;
  if (!isFlagsMap(flags)) {
    throw new FeatureFlagsPayloadError();
  }
  return flags;
}

/**
 * Busca as flags da audiência na montagem e a cada `refreshIntervalMs`.
 *
 * Uma leitura que falha (rede, 401, 5xx, corpo inválido) mantém as últimas
 * flags válidas e tenta de novo com espera crescente (`retryBaseDelayMs`,
 * dobrando até `refreshIntervalMs`), em vez de esperar a próxima consulta
 * periódica. O "tudo desligado" só vale enquanto nenhuma leitura deu certo:
 * uma falha no meio do expediente não pode esconder um recurso que a loja
 * tem ligado.
 */
export function FeatureFlagsProvider({
  endpoint,
  refreshIntervalMs = DEFAULT_REFRESH_INTERVAL,
  retryBaseDelayMs = DEFAULT_RETRY_BASE_DELAY,
  fetcher,
  children,
}: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  // O `refresh()` do contexto dispara a leitura do ciclo corrente do efeito,
  // com o `endpoint` e o `fetcher` que estão valendo.
  const loadRef = useRef<() => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    // Um ciclo por `endpoint`/`fetcher`: trocar qualquer um deles aborta as
    // leituras em voo do ciclo anterior, para que a resposta dada ao token ou
    // à loja de antes não caia por cima da nova.
    const controller = new AbortController();
    let consecutiveFailures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let lastStarted = 0;
    let lastApplied = 0;

    const cancelRetry = () => {
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
    };

    const load = async (): Promise<void> => {
      if (controller.signal.aborted) return;
      // A leitura que começa agora faz as vezes da nova tentativa agendada.
      cancelRetry();
      const request = ++lastStarted;
      try {
        const next = await requestFlags(endpoint, fetcher, controller.signal);
        if (controller.signal.aborted) return;
        consecutiveFailures = 0;
        // Uma resposta que chega depois da de uma leitura mais nova é descartada.
        if (request > lastApplied) {
          lastApplied = request;
          setFlags(next);
        }
      } catch (err) {
        if (controller.signal.aborted || (err as { name?: unknown } | null)?.name === 'AbortError') {
          return;
        }
        // Há uma leitura mais nova em voo: ela decide o que vem depois.
        if (request !== lastStarted) return;
        // Stale-while-error: as flags ficam como estão — as últimas válidas,
        // ou nenhuma (tudo desligado) se nenhuma leitura deu certo ainda.
        consecutiveFailures += 1;
        const nominal = retryDelayMs(consecutiveFailures, retryBaseDelayMs, refreshIntervalMs);
        const delay = Math.round(nominal * (0.5 + Math.random() * 0.5));
        // eslint-disable-next-line no-console
        console.warn(
          `[feature-flags] leitura falhou (${describeFailure(err)}); mantendo as últimas flags válidas, nova tentativa em ${Math.ceil(delay / 1000)} s`,
        );
        retryTimer = setTimeout(() => {
          retryTimer = undefined;
          void load();
        }, delay);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    loadRef.current = load;
    void load();
    const interval = setInterval(() => void load(), refreshIntervalMs);
    return () => {
      controller.abort();
      clearInterval(interval);
      cancelRetry();
    };
  }, [endpoint, fetcher, refreshIntervalMs, retryBaseDelayMs]);

  const refresh = useCallback(() => loadRef.current(), []);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ flags, isLoading, refresh }),
    [flags, isLoading, refresh],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// =============================================================================
//  Hook
// =============================================================================

/**
 * Returns the evaluated value of a feature flag for the current authenticated
 * context. Returns `false` for keys not present in the latest successful
 * response, and for every key while no read has succeeded yet.
 */
export function useFeatureFlag(key: string): boolean {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) return false;
  return ctx.flags[key] === true;
}

/** Hook providing imperative access to the provider state (refresh, isLoading). */
export function useFeatureFlagsContext(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlagsContext must be used within FeatureFlagsProvider');
  }
  return ctx;
}
